import { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Bird,
  CakeSlice,
  Cat,
  Check,
  Dog,
  Fish,
  Heart,
  Image as ImageIcon,
  Loader2,
  PawPrint,
  Rabbit,
  ShieldCheck,
  StickyNote,
  Trash2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { TextField, TextAreaField } from '@/components/text-field';
import { SelectField } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { SegmentedControl } from '@/components/ui/segmented';
import { AuthImage } from '@/components/auth-image';
import { cn } from '@/lib/utils';
import { PetPhotosSection } from './pet-photos-section';
import { PetMedicalSection } from './pet-medical-section';
import { PetVetRecordsSection } from './pet-vet-records-section';
import { ApiError } from '@/lib/api';
import { field_errors_from, form_error_message } from '@/lib/forms';
import {
  use_breeds,
  use_create_pet,
  use_delete_pet,
  use_pet,
  use_update_pet,
} from '@/features/pet/hooks';
import {
  type NeuterStatus,
  type PetSex,
  type PetSpecies,
  type PetWriteRequest,
} from '@/features/pet/types';

type Form = {
  name: string;
  species: PetSpecies;
  breed_id: string;
  mixed_breed_description: string;
  birth_date: string;
  approximate_age_months: string;
  sex: PetSex;
  neuter_status: NeuterStatus;
  weight_kg: string;
  color_markings: string;
  microchip_id: string;
  notes: string;
};

const EMPTY: Form = {
  name: '',
  species: 'DOG',
  breed_id: '',
  mixed_breed_description: '',
  birth_date: '',
  approximate_age_months: '',
  sex: 'UNKNOWN',
  neuter_status: 'UNKNOWN',
  weight_kg: '',
  color_markings: '',
  microchip_id: '',
  notes: '',
};

const SPECIES: { value: PetSpecies; label: string; icon: LucideIcon }[] = [
  { value: 'DOG', label: 'Dog', icon: Dog },
  { value: 'CAT', label: 'Cat', icon: Cat },
  { value: 'BIRD', label: 'Bird', icon: Bird },
  { value: 'RABBIT', label: 'Rabbit', icon: Rabbit },
  { value: 'FISH', label: 'Fish', icon: Fish },
  { value: 'OTHER', label: 'Other', icon: PawPrint },
];
const SPECIES_ICON = Object.fromEntries(SPECIES.map((s) => [s.value, s.icon])) as Record<PetSpecies, LucideIcon>;
const SPECIES_LABEL = Object.fromEntries(SPECIES.map((s) => [s.value, s.label])) as Record<PetSpecies, string>;

const SEX_SEGMENTS: { value: PetSex; label: string }[] = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'UNKNOWN', label: 'Unknown' },
];
const NEUTER_SEGMENTS: { value: NeuterStatus; label: string }[] = [
  { value: 'NEUTERED', label: 'Yes' },
  { value: 'NOT_NEUTERED', label: 'No' },
  { value: 'UNKNOWN', label: 'Not sure' },
];

const STEPS = ['Basics', 'Details', 'Notes'] as const;

/** Friendly age from a birth date or a months count (whichever is set). */
function age_label(birth_date: string, months_str: string): string | null {
  let months: number | null = null;
  if (birth_date && birth_date.length === 10) {
    const b = new Date(birth_date + 'T00:00:00');
    const now = new Date();
    months = (now.getFullYear() - b.getFullYear()) * 12 + (now.getMonth() - b.getMonth());
    if (now.getDate() < b.getDate()) months -= 1;
  } else if (months_str.trim() !== '') {
    const n = Number(months_str);
    if (Number.isFinite(n)) months = n;
  }
  if (months === null || months < 0) return null;
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y === 0) return `${m} mo`;
  if (m === 0) return `${y} yr`;
  return `${y} yr ${m} mo`;
}

function FormSection({ icon: Icon, title, children }: { icon: LucideIcon; title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-button bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </span>
        <h2 className="font-heading text-base font-bold">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export function PetEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const is_edit = !!id;

  const pet_q = use_pet(id);
  const create_m = use_create_pet();
  const update_m = use_update_pet(id ?? '');
  const delete_m = use_delete_pet();

  const [form, set_form] = useState<Form>(EMPTY);
  const [prefilled, set_prefilled] = useState(!is_edit);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});
  const [confirm_delete, set_confirm_delete] = useState(false);
  // Birthday vs. approximate age are mutually exclusive — toggle which to enter.
  const [age_mode, set_age_mode] = useState<'dob' | 'age'>('dob');
  // Wizard step (new-pet flow only).
  const [step, set_step] = useState(0);

  const breeds_for_species = use_breeds(form.species);
  const breed_name = breeds_for_species.data?.find((b) => b.id === form.breed_id)?.name;

  useEffect(() => {
    if (prefilled) return;
    if (pet_q.data) {
      const p = pet_q.data;
      set_form({
        name: p.name,
        species: p.species,
        breed_id: p.breed_id ?? '',
        mixed_breed_description: p.mixed_breed_description ?? '',
        birth_date: p.birth_date ?? '',
        approximate_age_months:
          p.approximate_age_months !== null ? String(p.approximate_age_months) : '',
        sex: p.sex,
        neuter_status: p.neuter_status,
        weight_kg: p.weight_kg !== null ? String(p.weight_kg) : '',
        color_markings: p.color_markings ?? '',
        microchip_id: p.microchip_id ?? '',
        notes: p.notes ?? '',
      });
      set_age_mode(!p.birth_date && p.approximate_age_months !== null ? 'age' : 'dob');
      set_prefilled(true);
    }
  }, [pet_q.data, prefilled]);

  function update<K extends keyof Form>(key: K, value: Form[K]) {
    set_form((f) => ({ ...f, [key]: value }));
    set_field_errors((fe) => {
      if (!(key in fe)) return fe;
      const next = { ...fe };
      delete next[key];
      return next;
    });
  }

  function switch_age_mode(mode: 'dob' | 'age') {
    set_age_mode(mode);
    set_form((f) => (mode === 'dob' ? { ...f, approximate_age_months: '' } : { ...f, birth_date: '' }));
  }

  /** Validate the numeric fields shared by step 2 + final submit. */
  function validate_metrics(): boolean {
    if (form.weight_kg.trim() !== '') {
      const n = Number(form.weight_kg);
      // The input mask allows digits and dots, so "1.2.3" reaches here as NaN —
      // "must be between 0 and 200" would be a confusing thing to say about it.
      if (!Number.isFinite(n)) {
        set_field_errors({ weight_kg: "That doesn't look like a number — try 18.5." });
        set_error('Please fix the highlighted fields below.');
        return false;
      }
      if (n <= 0 || n > 200) {
        set_field_errors({ weight_kg: 'Weight must be between 0 and 200 kg.' });
        set_error('Please fix the highlighted fields below.');
        return false;
      }
    }
    if (form.approximate_age_months.trim() !== '') {
      const n = Number(form.approximate_age_months);
      if (!Number.isInteger(n) || n < 0 || n > 600) {
        set_field_errors({ approximate_age_months: 'Age in months must be 0–600.' });
        set_error('Please fix the highlighted fields below.');
        return false;
      }
    }
    return true;
  }

  /** Validate only the fields owned by a wizard step. */
  function step_valid(s: number): boolean {
    set_error(null);
    set_field_errors({});
    if (s === 0 && form.name.trim().length < 1) {
      set_field_errors({ name: 'Name is required.' });
      set_error('Please fix the highlighted fields below.');
      return false;
    }
    if (s === 1) return validate_metrics();
    return true;
  }

  function build_request(): PetWriteRequest | null {
    if (form.name.trim().length < 1) {
      set_field_errors({ name: 'Name is required.' });
      set_error('Please fix the highlighted fields below.');
      return null;
    }
    if (!validate_metrics()) return null;
    const age = form.approximate_age_months.trim() !== '' ? Number(form.approximate_age_months) : undefined;
    const weight = form.weight_kg.trim() !== '' ? Number(form.weight_kg) : undefined;
    const trimmed = (s: string) => (s.trim() === '' ? undefined : s.trim());
    return {
      name: form.name.trim(),
      species: form.species,
      breed_id: form.breed_id || undefined,
      mixed_breed_description: trimmed(form.mixed_breed_description),
      birth_date: form.birth_date || undefined,
      approximate_age_months: age,
      sex: form.sex,
      neuter_status: form.neuter_status,
      weight_kg: weight,
      color_markings: trimmed(form.color_markings),
      microchip_id: trimmed(form.microchip_id),
      notes: trimmed(form.notes),
    };
  }

  async function save() {
    const req = build_request();
    if (!req) return;
    try {
      if (is_edit) await update_m.mutateAsync(req);
      else await create_m.mutateAsync(req);
      navigate('/app/pets');
    } catch (err) {
      set_field_errors(field_errors_from(err));
      set_error(
        err instanceof ApiError ? form_error_message(err, 'Could not save pet', { highlights_fields: true }) : 'Could not save pet',
      );
    }
  }

  // One submit handler: in the new-pet wizard, advance until the last step; on
  // the last step (or in edit mode) actually save. So Enter advances too.
  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    if (!is_edit && step < STEPS.length - 1) {
      if (step_valid(step)) set_step((s) => s + 1);
      return;
    }
    await save();
  }

  async function handle_delete() {
    if (!id) return;
    try {
      await delete_m.mutateAsync(id);
      navigate('/app/pets');
    } catch (err) {
      set_error(err instanceof ApiError ? err.message : 'Could not delete pet');
    }
  }

  function jump_to(s: number) {
    // Only allow jumping back to a completed step.
    if (s < step) {
      set_error(null);
      set_step(s);
    }
  }

  const saving = create_m.isPending || update_m.isPending;
  const loading_existing = is_edit && pet_q.isLoading;

  const HeroIcon = SPECIES_ICON[form.species] ?? PawPrint;
  const display_name = form.name.trim() || (is_edit ? pet_q.data?.name ?? 'Your pet' : 'Your new friend');
  const age = age_label(form.birth_date, form.approximate_age_months);
  const primary_photo = pet_q.data?.primary_photo_url;
  const is_last = step === STEPS.length - 1;

  /* ── Field groups, shared by the wizard (new) and the full page (edit) ── */

  const errorBanner = error && (
    <div
      className="rounded-input border-2 border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
      data-testid="pet-form-error"
      role="alert"
    >
      {error}
    </div>
  );

  const basicsSection = (
    <FormSection icon={PawPrint} title="Basics">
      <TextField
        label="Name"
        name="name"
        data-testid="field-pet-name"
        value={form.name}
        onChange={(e) => update('name', e.target.value)}
        error={field_errors.name}
        required
        maxLength={120}
        placeholder="e.g. Bruno"
      />
      <div className="flex flex-col gap-2">
        <Label>Species</Label>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6" role="radiogroup" aria-label="Species">
          {SPECIES.map((s) => {
            const selected = form.species === s.value;
            return (
              <button
                key={s.value}
                type="button"
                role="radio"
                aria-checked={selected}
                data-testid={`field-pet-species-${s.value}`}
                onClick={() => {
                  update('species', s.value);
                  update('breed_id', '');
                }}
                className={cn(
                  'clay-press flex flex-col items-center gap-1.5 rounded-input border-2 py-3 transition-all duration-fast',
                  selected
                    ? 'border-primary bg-primary/10 text-primary shadow-clay-sm'
                    : 'border-input bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground',
                )}
              >
                <s.icon className="h-6 w-6" />
                <span className="text-xs font-semibold">{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="breed">Breed</Label>
        <SelectField
          id="breed"
          data-testid="field-pet-breed"
          value={form.breed_id}
          onValueChange={(v) => update('breed_id', v)}
          placeholder="Select a breed (optional)"
          options={[
            { value: '', label: '— Unknown / mixed —' },
            ...(breeds_for_species.data?.map((b) => ({ value: b.id, label: b.name })) ?? []),
          ]}
        />
      </div>
      {form.breed_id === '' && (
        <TextField
          label="Mixed-breed description"
          name="mixed_breed_description"
          data-testid="field-pet-mixed"
          value={form.mixed_breed_description}
          onChange={(e) => update('mixed_breed_description', e.target.value)}
          optional
          maxLength={200}
          placeholder="e.g. Lab × Indie"
        />
      )}
    </FormSection>
  );

  const ageSection = (
    <FormSection icon={CakeSlice} title="Age">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex flex-col gap-2 sm:w-56">
          <Label>How do you want to enter it?</Label>
          <SegmentedControl
            value={age_mode}
            onChange={(m) => switch_age_mode(m)}
            aria-label="Age entry mode"
            options={[
              { value: 'dob', label: 'Birthday' },
              { value: 'age', label: 'Approx. age' },
            ]}
          />
        </div>
        <div className="flex flex-1 flex-col gap-2">
          {age_mode === 'dob' ? (
            <>
              <Label htmlFor="birth_date">Date of birth</Label>
              <DatePicker
                id="birth_date"
                data-testid="field-pet-dob"
                value={form.birth_date}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(v) => update('birth_date', v)}
                placeholder="Select date of birth"
              />
            </>
          ) : (
            <TextField
              label="Approximate age (months)"
              name="approximate_age_months"
              data-testid="field-pet-age"
              value={form.approximate_age_months}
              onChange={(e) =>
                update('approximate_age_months', e.target.value.replace(/\D/g, '').slice(0, 3))
              }
              error={field_errors.approximate_age_months}
              inputMode="numeric"
              placeholder="e.g. 18"
            />
          )}
        </div>
      </div>
    </FormSection>
  );

  const aboutSection = (
    <FormSection icon={Heart} title="About this pet">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Label>Sex</Label>
          <SegmentedControl
            value={form.sex}
            onChange={(v) => update('sex', v)}
            options={SEX_SEGMENTS}
            testId="field-pet-sex"
            aria-label="Sex"
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label>Neutered or spayed?</Label>
          <SegmentedControl
            value={form.neuter_status}
            onChange={(v) => update('neuter_status', v)}
            options={NEUTER_SEGMENTS}
            testId="field-pet-neuter"
            aria-label="Neuter status"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <TextField
          label="Weight (kg)"
          name="weight_kg"
          data-testid="field-pet-weight"
          value={form.weight_kg}
          onChange={(e) => update('weight_kg', e.target.value.replace(/[^\d.]/g, '').slice(0, 6))}
          error={field_errors.weight_kg}
          inputMode="decimal"
          optional
          placeholder="e.g. 18.5"
        />
        <TextField
          label="Colour / markings"
          name="color_markings"
          data-testid="field-pet-color"
          value={form.color_markings}
          onChange={(e) => update('color_markings', e.target.value)}
          optional
          maxLength={200}
          placeholder="e.g. Black with white chest"
        />
      </div>
      <TextField
        label="Microchip ID"
        name="microchip_id"
        data-testid="field-pet-microchip"
        value={form.microchip_id}
        onChange={(e) => update('microchip_id', e.target.value)}
        error={field_errors.microchip_id}
        optional
        maxLength={50}
        placeholder="15-digit chip number"
      />
    </FormSection>
  );

  const notesSection = (
    <FormSection icon={StickyNote} title="Notes">
      <TextAreaField
        label="Anything providers should know"
        name="notes"
        data-testid="field-pet-notes"
        value={form.notes}
        onChange={(e) => update('notes', e.target.value)}
        optional
        maxLength={2000}
        placeholder="Temperament, dietary needs, behaviour around strangers…"
      />
      <div className="flex items-center gap-2 rounded-input border-2 border-dashed border-brand-outline-variant/30 bg-secondary/30 px-4 py-3 text-xs text-muted-foreground">
        <span className="flex shrink-0 items-center gap-1.5 text-primary">
          <ImageIcon className="h-4 w-4" />
          <ShieldCheck className="h-4 w-4" />
        </span>
        Photos and health records (vaccinations, allergies, conditions) can be added right after
        you save.
      </div>
    </FormSection>
  );

  const WIZARD_CONTENT = [basicsSection, <>{ageSection}{aboutSection}</>, notesSection];

  const heroCard = (
    <div className="flex items-center gap-4 rounded-card border-2 border-brand-outline-variant/20 bg-gradient-to-br from-primary/10 via-card to-card p-5 shadow-clay">
      <div className="relative shrink-0">
        {primary_photo ? (
          <AuthImage
            path={`${primary_photo}?variant=thumb`}
            alt={display_name}
            className="h-20 w-20 rounded-2xl object-cover shadow-clay-sm"
          />
        ) : (
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl accent-gradient text-white shadow-clay-sm">
            <HeroIcon className="h-9 w-9" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">
          {is_edit ? 'Editing profile' : `Step ${step + 1} of ${STEPS.length}`}
        </p>
        <h1 className="truncate font-heading text-2xl font-extrabold leading-tight">{display_name}</h1>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <Chip>{SPECIES_LABEL[form.species]}</Chip>
          {breed_name && <Chip>{breed_name}</Chip>}
          {age && <Chip>{age}</Chip>}
          {form.sex !== 'UNKNOWN' && <Chip>{form.sex === 'MALE' ? 'Male' : 'Female'}</Chip>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5">
      <Button variant="ghost" size="sm" className="self-start" onClick={() => navigate('/app/pets')}>
        <ArrowLeft className="h-4 w-4" /> Back to my pets
      </Button>

      {loading_existing ? (
        <div className="flex items-center justify-center p-12 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {heroCard}

          {is_edit ? (
            /* ── Edit: everything on one page ── */
            <>
              <form onSubmit={handle_submit} noValidate>
                <Card>
                  <CardContent className="flex flex-col gap-8 p-6">
                    {errorBanner}
                    {basicsSection}
                    {ageSection}
                    {aboutSection}
                    {notesSection}
                  </CardContent>
                  <div className="flex flex-wrap items-center justify-between gap-3 border-t-2 border-brand-outline-variant/10 px-6 py-4">
                    {confirm_delete ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Delete this pet?</span>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={handle_delete}
                          disabled={delete_m.isPending}
                          data-testid="pet-delete-confirm"
                        >
                          {delete_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                          Yes, delete
                        </Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => set_confirm_delete(false)}>
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => set_confirm_delete(true)}
                        data-testid="pet-delete"
                      >
                        <Trash2 className="h-4 w-4" /> Delete
                      </Button>
                    )}
                    <Button type="submit" variant="gradient" disabled={saving} data-testid="pet-save">
                      {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                      Save changes
                    </Button>
                  </div>
                </Card>
              </form>

              <Card>
                <CardContent className="p-6">
                  <PetPhotosSection pet_id={id!} />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <PetMedicalSection pet_id={id!} />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <PetVetRecordsSection pet_id={id!} />
                </CardContent>
              </Card>
            </>
          ) : (
            /* ── New: guided wizard ── */
            <form onSubmit={handle_submit} noValidate>
              <Card>
                <div className="border-b-2 border-brand-outline-variant/10 px-6 py-4">
                  <Stepper current={step} onJump={jump_to} />
                </div>
                <CardContent className="flex flex-col gap-8 p-6">
                  {errorBanner}
                  {WIZARD_CONTENT[step]}
                </CardContent>
                <div className="flex items-center justify-between gap-3 border-t-2 border-brand-outline-variant/10 px-6 py-4">
                  {step > 0 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => jump_to(step - 1)}
                      data-testid="pet-wizard-back"
                    >
                      <ArrowLeft className="h-4 w-4" /> Back
                    </Button>
                  ) : (
                    <span />
                  )}
                  {is_last ? (
                    <Button type="submit" variant="gradient" disabled={saving} data-testid="pet-save">
                      {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                      Add pet
                    </Button>
                  ) : (
                    <Button type="submit" variant="gradient" data-testid="pet-wizard-next">
                      Continue <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </Card>
            </form>
          )}
        </>
      )}
    </div>
  );
}

function Stepper({ current, onJump }: { current: number; onJump: (i: number) => void }) {
  return (
    <ol className="flex items-center gap-2" data-testid="pet-wizard-steps">
      {STEPS.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <Fragment key={label}>
            <li>
              <button
                type="button"
                disabled={i >= current}
                onClick={() => onJump(i)}
                className={cn(
                  'flex items-center gap-2 rounded-full py-1.5 pl-1.5 pr-3 text-sm font-semibold transition-colors',
                  active ? 'bg-primary/10 text-primary' : done ? 'text-foreground hover:bg-secondary' : 'text-muted-foreground',
                )}
              >
                <span
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full text-xs',
                    active
                      ? 'bg-primary text-primary-foreground'
                      : done
                        ? 'bg-primary/20 text-primary'
                        : 'bg-secondary text-muted-foreground',
                  )}
                >
                  {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span className="hidden sm:inline">{label}</span>
              </button>
            </li>
            {i < STEPS.length - 1 && <li className="h-0.5 flex-1 rounded-full bg-secondary" aria-hidden />}
          </Fragment>
        );
      })}
    </ol>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-card/80 px-2.5 py-0.5 text-xs font-semibold text-foreground shadow-clay-sm">
      {children}
    </span>
  );
}
